import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, explainSaveError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { Save, Plus, Trash2, Sparkles, ChevronDown, ChevronRight, PackageOpen, ShieldCheck } from "lucide-react";
import ApprovedTradePricingSection from "@/components/pricing/ApprovedTradePricingSection";

const TEMPLATE_UNIT_OPTIONS = [
  "each", "sq ft", "square", "LF", "bundle", "roll", "box", "gallon",
  "cubic yard", "ton", "sheet", "board", "board-ft", "panel", "stair",
  "room", "fixture", "opening", "door", "window", "coat", "layer",
  "trip", "day", "hour", "job", "allowance",
];

// Contractor-declared pricing style. Feb 2026 UX pass — the picker now
// expresses the FOUR Product Owner-approved contractor choices. Legacy
// values (``labor_materials``, ``hybrid``) still round-trip through
// PUT/GET but map to the "Other / Help Me Set It Up" advanced path for
// display purposes.
const PRICING_STYLE_OPTIONS = [
  {
    id: "unit",
    title: "All-in Price by Unit",
    subtitle: "Recommended",
    desc: "One complete selling price per repeatable unit — materials, labor, overhead, and profit already included. Works for square, sq ft, linear ft, fixture, opening, item, and more.",
  },
  {
    id: "flat",
    title: "All-in Price by Job or System",
    desc: "One complete selling price per job, system, or defined assembly — materials, labor, overhead, and profit included. Common for HVAC and other system-based trades.",
  },
  {
    id: "learn_from_history",
    title: "Learn From My Approved Estimates",
    desc: "Levi reviews your approved past estimates and suggests pricing. You review and approve every price before it becomes a rule.",
  },
  {
    id: "help_me_setup",
    title: "Other / Help Me Set It Up",
    desc: "Not sure yet? Levi routes you into the existing advanced setup — labor + materials, T&M, flat, or a mix — with contractor control preserved throughout.",
  },
];
const DEFAULT_PRICING_STYLE = "unit";
const LEGACY_STYLE_MAP = {
  labor_materials: "help_me_setup",
  hybrid: "help_me_setup",
};
const _normalizeStyleForPicker = (val) =>
  LEGACY_STYLE_MAP[val] || val || DEFAULT_PRICING_STYLE;

const Field = ({ label, hint, suffix, prefix, ...rest }) => (
  <div>
    <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">{label}</label>
    <div className="flex items-stretch">
      {prefix && (
        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-[var(--jc-border-strong)] bg-[var(--jc-bg)] text-sm font-mono-jc text-[var(--jc-text-soft)]">
          {prefix}
        </span>
      )}
      <input
        {...rest}
        className={`w-full px-3.5 py-2.5 border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] ${
          prefix ? "rounded-r-md" : suffix ? "rounded-l-md" : "rounded-md"
        }`}
      />
      {suffix && (
        <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-[var(--jc-border-strong)] bg-[var(--jc-bg)] text-sm font-mono-jc text-[var(--jc-text-soft)]">
          {suffix}
        </span>
      )}
    </div>
    {hint && <p className="text-xs text-[var(--jc-text-soft)] mt-1">{hint}</p>}
  </div>
);

const TArea = ({ label, ...rest }) => (
  <div>
    <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">{label}</label>
    <textarea
      {...rest}
      rows={3}
      className="w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
    />
  </div>
);

const PricingProfile = () => {
  const { t } = useLang();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templatesMeta, setTemplatesMeta] = useState(null);
  const [openTrades, setOpenTrades] = useState({});
  const [openSystems, setOpenSystems] = useState({});
  const [loadingTrade, setLoadingTrade] = useState("");
  // Dismissable one-time banners for untagged legacy rules (per trade).
  const [tagBannerDismissed, setTagBannerDismissed] = useState({});
  // Feb 2026 UX pass: Advanced Pricing Defaults + Starter Trade Templates
  // are collapsed by default so the first-run experience is not
  // labor-rate-first / template-overload-first.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  // Secondary toggle inside Advanced Pricing Defaults. When the contractor
  // picked a non-hourly default style (unit / flat), Labor rate + Overhead +
  // Profit margin collapse behind this toggle so they don't feel prescriptive.
  // Existing saved values are preserved on the profile — this is a display
  // affordance only. See ``hidesLaborByDefault`` below.
  const [laborSettingsOpen, setLaborSettingsOpen] = useState(false);
  // iter55d · Redirect incomplete accounts back into unified Setup.
  const [readiness, setReadiness] = useState(null);

  useEffect(() => {
    api.get("/pricing-profile").then((r) => setP(r.data));
    api.get("/pricing-profile/trade-templates").then((r) => setTemplatesMeta(r.data)).catch(() => {});
    // iter55d · Incomplete accounts must not use Pricing Profile as a
    // competing onboarding path. Redirect them into the unified Setup
    // Section 4 (`/app/setup#trade-pricing`). Ready accounts see this
    // page as "Pricing Settings" (maintenance only).
    api.get("/onboarding-status")
      .then((r) => setReadiness(r.data || null))
      .catch(() => setReadiness({ pricing_ready: true })); // fail open — don't strand users
  }, []);

  if (readiness && readiness.pricing_ready === false) {
    return <Navigate to="/app/setup#trade-pricing" replace />;
  }
  if (!p) return <DashboardLayout title={t("pages.pricing.loading_title")} subtitle={t("pages.pricing.loading_subtitle")}><div /></DashboardLayout>;

  const u = (k) => (e) => setP({ ...p, [k]: e.target.type === "number" ? parseFloat(e.target.value || 0) : e.target.value });

  // --- Material rules / Trade Material Templates helpers ---
  const rules = p.material_rules || [];
  const supportedTrades = templatesMeta?.supported_trades || [];
  const tradeSystems = templatesMeta?.trade_systems || {};
  // Group rules by trade → system_type (or "" for untagged legacy rules).
  const rulesByTradeSystem = rules.reduce((acc, r) => {
    const t = r.trade || "Other";
    const s = (r.system_type || "").trim();
    acc[t] = acc[t] || {};
    acc[t][s] = acc[t][s] || [];
    acc[t][s].push(r);
    return acc;
  }, {});
  // Surface all trades that either have rules already OR have a starter template available.
  const allTrades = Array.from(new Set([...Object.keys(rulesByTradeSystem), ...supportedTrades]));

  const toggleTrade = (t) => setOpenTrades((s) => ({ ...s, [t]: !s[t] }));
  const toggleSystem = (key) => setOpenSystems((s) => ({ ...s, [key]: !s[key] }));

  const loadTemplate = async (trade, systemType) => {
    const existingForSystem = (rulesByTradeSystem[trade] || {})[systemType] || [];
    if (existingForSystem.length > 0) {
      if (!window.confirm(`Replace your existing ${trade} → ${systemType} rules with the starter template? Your edits will be lost.`)) return;
    }
    setLoadingTrade(`${trade}::${systemType}`);
    try {
      const { data } = await api.post("/pricing-profile/load-template", { trade, system_type: systemType, mode: "replace" });
      setP(data);
      setOpenTrades((s) => ({ ...s, [trade]: true }));
      setOpenSystems((s) => ({ ...s, [`${trade}::${systemType}`]: true }));
      toast.success(`Loaded ${trade} — ${systemType} starter template`);
    } catch {
      toast.error(`Could not load ${trade} → ${systemType} template`);
    } finally {
      setLoadingTrade("");
    }
  };

  const tagUntaggedRules = async (trade, systemType) => {
    try {
      const { data } = await api.post("/pricing-profile/tag-untagged-rules", { trade, system_type: systemType });
      setP(data.profile);
      toast.success(`Tagged ${data.updated} ${trade} rule${data.updated === 1 ? "" : "s"} as ${systemType}`);
      setTagBannerDismissed((s) => ({ ...s, [trade]: true }));
    } catch {
      toast.error("Could not tag rules");
    }
  };

  const updateRule = (ruleId, key, val) => {
    const numKeys = ["material_qty_per_unit", "material_unit_cost", "labor_qty_per_unit", "labor_unit_cost", "markup_pct"];
    const next = (p.material_rules || []).map((r) =>
      r.id === ruleId ? { ...r, [key]: numKeys.includes(key) ? (val === "" ? null : parseFloat(val || 0)) : val } : r
    );
    setP({ ...p, material_rules: next });
  };

  const removeRule = (ruleId) => {
    setP({ ...p, material_rules: (p.material_rules || []).filter((r) => r.id !== ruleId) });
  };

  const addBlankRule = (trade, systemType = "") => {
    const id = (crypto.randomUUID && crypto.randomUUID()) || `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const rule = {
      id, trade, system_type: systemType, name: "", description: "",
      unit: "each", material_basis_unit: "each", material_qty_per_unit: 1,
      material_unit_cost: 0, material_required: true,
      labor_basis: "hour", labor_qty_per_unit: 0, labor_unit_cost: 0,
      markup_pct: null, notes: "",
    };
    setP({ ...p, material_rules: [...(p.material_rules || []), rule] });
    setOpenTrades((s) => ({ ...s, [trade]: true }));
    if (systemType) setOpenSystems((s) => ({ ...s, [`${trade}::${systemType}`]: true }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { id, user_id, updated_at, ...payload } = p;
      const { data } = await api.put("/pricing-profile", payload);
      setP(data);
      toast.success("Pricing profile saved");
    } catch (err) {
      toast.error(explainSaveError(err, "pricing profile"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout
      title={t("pages.pricing.title")}
      subtitle={t("pages.pricing.subtitle")}
      actions={
        <div className="flex items-center gap-2">
          <Link
            to="/app/pricing-profile/materials"
            data-testid="pricing-materials-link"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
          >
            <PackageOpen size={14} /> Materials Price Book
          </Link>
          <button
            onClick={save}
            disabled={saving}
            data-testid="pricing-save-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
          >
            <Save size={14} /> {saving ? t("pricing_form.saving") : t("pricing_form.save_all")}
          </button>
        </div>
      }
    >
      <div className="max-w-5xl space-y-6">
        {/* iter55d · Maintenance-mode notice: this page is now Pricing Settings,
             not initial setup. Initial setup lives in the Contractor Setup Wizard. */}
        <div
          data-testid="pricing-settings-maintenance-notice"
          className="jc-card p-4 flex items-start gap-3 border-l-4 border-[var(--jc-blue)]"
        >
          <ShieldCheck size={18} className="text-[var(--jc-blue)] shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm leading-relaxed text-[var(--jc-text)]">
            <span className="font-semibold">{t("pricing_extra.maintenance_headline")}</span>{" "}
            <span className="text-[var(--jc-text-soft)]">
              {t("pricing_extra.maintenance_body")}{" "}
              <Link to="/app/setup#trade-pricing" className="underline">{t("pricing_extra.setup_wizard")}</Link>.
            </span>
          </div>
        </div>

        <section className="jc-card p-6">
          <div className="overline mb-4">{t("pricing_form.section_summary")}</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("pricing_extra.company_name")} value={p.company_name} onChange={u("company_name")} data-testid="profile-company-input" />
            <Field label={t("pricing_extra.trade_primary")} value={p.trade} onChange={u("trade")} placeholder={t("pricing_extra.trade_primary_placeholder")} data-testid="profile-trade-input" />
          </div>
        </section>

        {/* Slice 2B1 · Approved Trade Pricing — canonical list + inline
             edit of the tenant's approved-reusable PricingRules. Sits
             immediately below Company so it is the FIRST thing the
             contractor sees after landing on Pricing Settings.
           Slice 2D4 · The heading below is the user-facing bilingual
             label for this section. Use t() so the section overline
             stays in sync with the app-language toggle. */}
        <div className="px-1" data-testid="pricing-approved-rules-heading">
          <div className="overline">{t("pricing_form.section_rules")}</div>
          <p className="text-xs text-[var(--jc-text-soft)] mt-1">
            {t("pricing_form.add_rule")} · {t("pricing_form.section_labor")} · {t("pricing_form.section_materials")}
          </p>
        </div>
        <ApprovedTradePricingSection />

        {/* Default Pricing Style — Feb 2026 UX pass. UX-only, not math. */}
        <section className="jc-card p-6" data-testid="default-pricing-style-section">
          <div className="overline mb-1">{t("pricing_extra.style_overline")}</div>
          <h3 className="font-display text-lg mb-1">{t("pricing_extra.style_heading")}</h3>
          <p className="text-sm text-[var(--jc-text-soft)] mb-4">
            {t("pricing_extra.style_body")}
          </p>
          <div
            role="radiogroup"
            aria-label="Default pricing style"
            className="grid sm:grid-cols-2 gap-3"
          >
            {PRICING_STYLE_OPTIONS.map((opt) => {
              const active = _normalizeStyleForPicker(p.default_pricing_style) === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`pricing-style-${opt.id}`}
                  onClick={() => setP({ ...p, default_pricing_style: opt.id })}
                  className={`text-left rounded-md border p-4 transition ${
                    active
                      ? "border-[var(--jc-blue)] bg-[var(--jc-blue)]/5 ring-2 ring-[var(--jc-blue)]/20"
                      : "border-[var(--jc-border-strong)] hover:border-[var(--jc-blue)]/50 hover:bg-[var(--jc-bg)]"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span
                      aria-hidden="true"
                      className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ${
                        active ? "border-[var(--jc-blue)] bg-[var(--jc-blue)]" : "border-[var(--jc-border-strong)]"
                      }`}
                    >
                      {active && <span className="block w-1.5 h-1.5 rounded-full bg-white m-auto mt-[3px]" />}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-sm text-[var(--jc-text)]">{opt.title}</div>
                        {opt.subtitle && (
                          <span
                            data-testid={`pricing-style-${opt.id}-badge`}
                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] border border-[var(--jc-blue)]/25"
                          >
                            {opt.subtitle}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[var(--jc-text-soft)] mt-1 leading-relaxed">{opt.desc}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="jc-card p-6">
          <div className="overline mb-4">{t("pricing_extra.job_defaults")}</div>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Tax rate" type="number" suffix="%" value={p.tax_rate} onChange={u("tax_rate")} data-testid="profile-tax-input" />
            <Field label="Trip / inspection charge" type="number" prefix="$" value={p.trip_charge} onChange={u("trip_charge")} data-testid="profile-trip-charge-input" />
            <Field label="Default markup / target profit" type="number" suffix="%" value={p.markup_pct} onChange={u("markup_pct")} hint="Applied when a line item uses labor + materials. Ignored for flat or unit-priced lines." data-testid="profile-markup-input" />
          </div>
        </section>

        {/* Advanced Pricing Defaults — collapsed by default (Feb 2026 UX pass) */}
        <section className="jc-card p-6" data-testid="advanced-pricing-section">
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            data-testid="advanced-pricing-toggle"
            aria-expanded={advancedOpen}
            className="w-full flex items-start justify-between gap-3 text-left"
          >
            <div>
              <div className="overline">Optional</div>
              <h3 className="font-display text-lg mt-1">Advanced Pricing Defaults</h3>
              <p className="text-xs text-[var(--jc-text-soft)] mt-1 max-w-2xl leading-relaxed">
                These settings are optional defaults. You can still price each estimate line by
                unit price, flat price, or labor/materials.
              </p>
            </div>
            {advancedOpen ? <ChevronDown size={18} className="mt-1 text-[var(--jc-text-soft)] shrink-0" /> : <ChevronRight size={18} className="mt-1 text-[var(--jc-text-soft)] shrink-0" />}
          </button>
          {advancedOpen && (
            <div className="mt-5 space-y-5" data-testid="advanced-pricing-body">
              {(() => {
                // Feb 2026 UX pass — when the contractor priced by unit or flat,
                // Labor rate / Overhead / Profit margin are hidden behind a
                // secondary toggle so the "Advanced" surface doesn't feel
                // labor-rate-heavy for non-hourly contractors. Values already
                // saved on the profile are preserved regardless of visibility.
                const currentStyle = p.default_pricing_style || DEFAULT_PRICING_STYLE;
                // "unit", "flat", and "learn_from_history" all mean the
                // contractor priced all-in and does not need the labor
                // fields exposed by default. "help_me_setup" and legacy
                // labor_materials / hybrid values keep labor visible.
                const hidesLaborByDefault =
                  currentStyle === "unit" ||
                  currentStyle === "flat" ||
                  currentStyle === "learn_from_history";
                const laborFields = (
                  <div className="grid md:grid-cols-3 gap-4" data-testid="advanced-labor-fields">
                    <Field label="Labor rate" type="number" prefix="$" value={p.labor_rate} onChange={u("labor_rate")} hint="Optional. Only used when a line item uses labor + materials pricing." data-testid="profile-labor-rate-input" />
                    <Field label="Overhead" type="number" suffix="%" value={p.overhead_pct} onChange={u("overhead_pct")} data-testid="profile-overhead-input" />
                    <Field label="Profit margin" type="number" suffix="%" value={p.profit_margin_pct} onChange={u("profit_margin_pct")} data-testid="profile-profit-margin-input" />
                  </div>
                );
                return hidesLaborByDefault ? (
                  <div data-testid="labor-settings-group">
                    <button
                      type="button"
                      onClick={() => setLaborSettingsOpen((v) => !v)}
                      data-testid="labor-settings-toggle"
                      aria-expanded={laborSettingsOpen}
                      className="w-full flex items-start justify-between gap-3 text-left rounded-md border border-[var(--jc-border)] px-3 py-2.5 hover:bg-[var(--jc-bg)]"
                    >
                      <div>
                        <div className="text-sm font-semibold text-[var(--jc-text)]">
                          Show hourly / labor settings
                        </div>
                        <div className="text-xs text-[var(--jc-text-soft)] mt-0.5 leading-relaxed">
                          Only use these if you also create labor + materials line items.
                        </div>
                      </div>
                      {laborSettingsOpen
                        ? <ChevronDown size={16} className="mt-1 text-[var(--jc-text-soft)] shrink-0" />
                        : <ChevronRight size={16} className="mt-1 text-[var(--jc-text-soft)] shrink-0" />}
                    </button>
                    {laborSettingsOpen && <div className="mt-4">{laborFields}</div>}
                  </div>
                ) : laborFields;
              })()}
              <div className="grid md:grid-cols-2 gap-4">
                <TArea label="Supplier preferences" value={p.supplier_preferences} onChange={u("supplier_preferences")} data-testid="profile-supplier-prefs-input" />
                <TArea label="Standard material notes" value={p.standard_material_notes} onChange={u("standard_material_notes")} data-testid="profile-material-notes-input" />
                <TArea label="Standard scope rules" value={p.standard_scope_rules} onChange={u("standard_scope_rules")} data-testid="profile-scope-rules-input" />
              </div>
            </div>
          )}
        </section>

        {/* Starter Trade Templates — collapsed by default (Feb 2026 UX pass) */}
        <section className="jc-card p-6" data-testid="trade-templates-section">
          <button
            type="button"
            onClick={() => setTemplatesOpen((v) => !v)}
            data-testid="trade-templates-toggle"
            aria-expanded={templatesOpen}
            className="w-full flex items-start justify-between gap-3 text-left"
          >
            <div>
              <div className="overline">Optional</div>
              <h3 className="font-display text-lg mt-1">Starter Trade Templates</h3>
              <p className="text-xs text-[var(--jc-text-soft)] mt-1 max-w-2xl leading-relaxed">
                Optional starter templates can help you preload common materials. You can skip this
                and add materials later — every value is editable and you own the final pricing.
              </p>
            </div>
            {templatesOpen ? <ChevronDown size={18} className="mt-1 text-[var(--jc-text-soft)] shrink-0" /> : <ChevronRight size={18} className="mt-1 text-[var(--jc-text-soft)] shrink-0" />}
          </button>

          {templatesOpen && (
            <div className="mt-5" data-testid="trade-templates-body">
              <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-900 mb-4">
                <b>These are editable defaults, not guaranteed market pricing.</b> Adjust every value to match
                your supplier costs and labor rates. You own and approve your pricing.
              </div>

              {allTrades.length === 0 ? (
                <div className="text-sm text-[var(--jc-text-soft)]">
                  Loading trade templates…
                </div>
              ) : (
                <div className="space-y-2">
                  {allTrades.map((trade) => {
                    const tradeRulesBySys = rulesByTradeSystem[trade] || {};
                    const supportedSystems = (tradeSystems[trade]?.systems || []);
                    // List of system_types to render for this trade — union of supported + any
                    // system_types the contractor already has (including untagged "").
                    const systemsToShow = Array.from(new Set([
                      ...supportedSystems.map((s) => s.name),
                      ...Object.keys(tradeRulesBySys),
                    ]));
                    const totalRules = Object.values(tradeRulesBySys).reduce((acc, arr) => acc + arr.length, 0);
                    const tradeOpen = !!openTrades[trade];
                    const untaggedCount = (tradeRulesBySys[""] || []).length;
                    const defaultSystemFromTemplate = supportedSystems[0]?.name || "";
                    const showTagBanner = untaggedCount > 0 && !tagBannerDismissed[trade] && defaultSystemFromTemplate;
                    return (
                      <div key={trade} className="rounded-md border border-[var(--jc-border)]" data-testid={`trade-section-${trade.toLowerCase()}`}>
                        <button
                          onClick={() => toggleTrade(trade)}
                          data-testid={`trade-toggle-${trade.toLowerCase()}`}
                          className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[var(--jc-bg)]"
                        >
                          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--jc-text)]">
                            {tradeOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            {trade}
                            <span className="text-xs text-[var(--jc-text-soft)] font-normal">
                              ({totalRules} rule{totalRules === 1 ? "" : "s"} across {systemsToShow.length} system{systemsToShow.length === 1 ? "" : "s"})
                            </span>
                          </div>
                        </button>

                        {tradeOpen && (
                          <div className="px-3 pb-3 space-y-2 border-t border-[var(--jc-border)]">
                            {/* One-time banner: tag untagged legacy rules */}
                            {showTagBanner && (
                              <div
                                data-testid={`tag-banner-${trade.toLowerCase()}`}
                                className="mt-3 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2.5 flex items-start gap-3"
                              >
                                <div className="flex-1 text-xs text-yellow-900">
                                  <b>Your existing {trade} rules do not have a system type.</b>
                                  {" "}Tag them as <b>{defaultSystemFromTemplate}</b>? This is reversible — you can edit any rule&apos;s system_type later.
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => tagUntaggedRules(trade, defaultSystemFromTemplate)}
                                    data-testid={`tag-banner-confirm-${trade.toLowerCase()}`}
                                    className="px-2 py-1 text-[11px] font-semibold rounded jc-btn-primary"
                                  >
                                    Tag as {defaultSystemFromTemplate}
                                  </button>
                                  <button
                                    onClick={() => setTagBannerDismissed((s) => ({ ...s, [trade]: true }))}
                                    data-testid={`tag-banner-dismiss-${trade.toLowerCase()}`}
                                    className="px-2 py-1 text-[11px] font-semibold rounded border border-[var(--jc-border-strong)] hover:bg-white"
                                  >
                                    Dismiss
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* System-type sub-accordions */}
                            {systemsToShow.length === 0 ? (
                              <div className="text-xs text-[var(--jc-text-soft)] mt-3">
                                No system types saved yet. {supportedSystems.length > 0 ? "Load a starter template above to begin." : "Add a custom rule below."}
                              </div>
                            ) : (
                              systemsToShow.map((sysName) => {
                                const sysKey = `${trade}::${sysName}`;
                                const sysRules = tradeRulesBySys[sysName] || [];
                                const hasTemplate = supportedSystems.some((s) => s.name === sysName);
                                const sysOpen = !!openSystems[sysKey];
                                const displayName = sysName || "Unspecified system";
                                return (
                                  <div key={sysKey} className="rounded border border-[var(--jc-border)] bg-[var(--jc-bg)]/30" data-testid={`system-section-${trade.toLowerCase()}-${(sysName || "untagged").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                    <button
                                      onClick={() => toggleSystem(sysKey)}
                                      data-testid={`system-toggle-${trade.toLowerCase()}-${(sysName || "untagged").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-white"
                                    >
                                      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--jc-text)]">
                                        {sysOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                        {trade} — {displayName}
                                        <span className="text-[10px] text-[var(--jc-text-soft)] font-normal">
                                          ({sysRules.length} rule{sysRules.length === 1 ? "" : "s"})
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {sysRules.length === 0 && hasTemplate && (
                                          <span
                                            onClick={(e) => { e.stopPropagation(); loadTemplate(trade, sysName); }}
                                            data-testid={`system-load-template-${trade.toLowerCase()}-${sysName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded jc-btn-primary"
                                          >
                                            <Sparkles size={10} /> {loadingTrade === sysKey ? "Loading…" : `Load ${displayName} starter`}
                                          </span>
                                        )}
                                        {sysRules.length > 0 && hasTemplate && (
                                          <span
                                            onClick={(e) => { e.stopPropagation(); loadTemplate(trade, sysName); }}
                                            data-testid={`system-reset-${trade.toLowerCase()}-${sysName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded border border-[var(--jc-border-strong)] hover:bg-white"
                                          >
                                            Reset to template
                                          </span>
                                        )}
                                      </div>
                                    </button>
                                    {sysOpen && (
                                      <div className="px-3 pb-3 space-y-2 border-t border-[var(--jc-border)] bg-white">
                                        {sysRules.length === 0 ? (
                                          <div className="text-xs text-[var(--jc-text-soft)] mt-3">
                                            No rules yet.{hasTemplate ? " Click \"Load starter\" above or " : " "}
                                            <button
                                              onClick={() => addBlankRule(trade, sysName)}
                                              data-testid={`system-add-blank-${trade.toLowerCase()}-${(sysName || "untagged").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                              className="underline text-[var(--jc-blue)]"
                                            >
                                              add a custom rule
                                            </button>.
                                          </div>
                                        ) : (
                                          sysRules.map((r) => (
                                            <RuleCard
                                              key={r.id}
                                              rule={r}
                                              updateRule={updateRule}
                                              removeRule={removeRule}
                                            />
                                          ))
                                        )}
                                        <button
                                          onClick={() => addBlankRule(trade, sysName)}
                                          data-testid={`system-add-rule-${trade.toLowerCase()}-${(sysName || "untagged").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--jc-blue)] hover:underline"
                                        >
                                          <Plus size={12} /> Add rule
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="jc-card p-6">
          <div className="overline mb-4">{t("pricing_extra.terms")}</div>
          <div
            data-testid="spanish-output-note"
            className="mb-4 rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)] px-3 py-2 text-xs leading-relaxed text-[var(--jc-text-soft)]"
          >
            <span className="font-semibold text-[var(--jc-text)]">Spanish output note:</span>{" "}
            If you leave the default wording in place, JobClose AI can automatically generate Spanish customer-facing documents when Spanish is selected. If you customize these terms, your custom wording will always be used so you stay in control.
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <TArea label="Payment terms" value={p.payment_terms} onChange={u("payment_terms")} />
            <TArea label="Contract terms" value={p.contract_terms} onChange={u("contract_terms")} />
            <TArea label="Proposal disclaimer" value={p.proposal_disclaimer} onChange={u("proposal_disclaimer")} />
            <TArea label="Invoice terms" value={p.invoice_terms} onChange={u("invoice_terms")} />
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

const RuleCard = ({ rule, updateRule, removeRule }) => {
  const u = (k) => (e) => updateRule(rule.id, k, e.target.value);
  // Compose labor basis options similar to the estimate line-item card.
  const laborBasisOptions = Array.from(new Set(["hour", rule.unit, "job"]));
  const matBasisOptions = Array.from(new Set([rule.unit, ...TEMPLATE_UNIT_OPTIONS]));
  return (
    <div
      data-testid={`rule-card-${rule.id}`}
      className="rounded border border-[var(--jc-border)] p-3 bg-white mt-2"
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 flex items-center gap-2 flex-wrap">
          <input
            data-testid={`rule-name-${rule.id}`}
            value={rule.name || ""}
            onChange={u("name")}
            placeholder="Rule name (e.g. Asphalt shingles)"
            className="flex-1 min-w-[180px] px-2 py-1 rounded border border-[var(--jc-border)] text-sm font-semibold"
          />
          {(rule.system_type || "").trim() && (
            <span
              data-testid={`rule-system-pill-${rule.id}`}
              className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] border border-[var(--jc-blue)]/30"
              title={`Belongs to ${rule.trade} → ${rule.system_type}`}
            >
              {rule.system_type}
            </span>
          )}
        </div>
        <button
          onClick={() => removeRule(rule.id)}
          data-testid={`rule-remove-${rule.id}`}
          className="text-[var(--jc-text-soft)] hover:text-[var(--jc-red)] mt-1"
          aria-label="Remove rule"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <input
        value={rule.description || ""}
        onChange={u("description")}
        placeholder="Description (used in customer-facing line item)"
        className="w-full mt-1.5 px-2 py-1 rounded border border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)]"
      />

      <div className="grid md:grid-cols-3 gap-2 mt-2 text-xs">
        {/* Quantity unit */}
        <div className="rounded border border-[var(--jc-border)] p-2">
          <div className="overline text-[10px] mb-1">Quantity unit</div>
          <select
            data-testid={`rule-unit-${rule.id}`}
            value={rule.unit || "each"}
            onChange={u("unit")}
            className="w-full px-2 py-1 rounded border border-[var(--jc-border)] bg-white"
          >
            {TEMPLATE_UNIT_OPTIONS.includes(rule.unit) ? null : <option value={rule.unit}>{rule.unit}</option>}
            {TEMPLATE_UNIT_OPTIONS.map((u2) => <option key={u2} value={u2}>{u2}</option>)}
          </select>
          <label className="flex items-center gap-1.5 mt-1.5 text-[11px] text-[var(--jc-text-soft)]">
            <input
              type="checkbox"
              checked={rule.material_required !== false}
              onChange={(e) => updateRule(rule.id, "material_required", e.target.checked)}
              data-testid={`rule-material-required-${rule.id}`}
            />
            Materials required
          </label>
        </div>

        {/* Material */}
        <div className="rounded border border-[var(--jc-border)] p-2">
          <div className="overline text-[10px] mb-1">Material</div>
          <div className="flex items-center gap-1">
            <select
              data-testid={`rule-mat-basis-${rule.id}`}
              value={rule.material_basis_unit || rule.unit}
              onChange={u("material_basis_unit")}
              className="flex-1 px-1.5 py-1 rounded border border-[var(--jc-border)] bg-white text-[11px]"
            >
              {matBasisOptions.map((u2) => <option key={u2} value={u2}>per {u2}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1 mt-1">
            <input
              data-testid={`rule-mat-qty-per-unit-${rule.id}`}
              type="number"
              step="any"
              value={rule.material_qty_per_unit ?? 1}
              onChange={u("material_qty_per_unit")}
              className="w-16 text-right px-1 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
              title={`${rule.material_basis_unit || rule.unit} per ${rule.unit}`}
            />
            <span className="text-[var(--jc-text-soft)] text-[10px]">{rule.material_basis_unit || rule.unit}/{rule.unit}</span>
          </div>
          <div className="flex items-center gap-1 mt-1">
            <span className="text-[var(--jc-text-soft)]">$</span>
            <input
              data-testid={`rule-mat-cost-${rule.id}`}
              type="number"
              step="any"
              value={rule.material_unit_cost ?? 0}
              onChange={u("material_unit_cost")}
              className="w-20 text-right px-1 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
            />
            <span className="text-[var(--jc-text-soft)] text-[10px]">/ {rule.material_basis_unit || rule.unit}</span>
          </div>
        </div>

        {/* Labor */}
        <div className="rounded border border-[var(--jc-border)] p-2">
          <div className="overline text-[10px] mb-1">Labor</div>
          <select
            data-testid={`rule-labor-basis-${rule.id}`}
            value={rule.labor_basis || "hour"}
            onChange={u("labor_basis")}
            className="w-full px-1.5 py-1 rounded border border-[var(--jc-border)] bg-white text-[11px]"
          >
            {laborBasisOptions.map((b) => (
              <option key={b} value={b}>{b === "hour" ? "per hour" : b === "job" ? "per job" : `per ${b}`}</option>
            ))}
          </select>
          <div className="flex items-center gap-1 mt-1">
            <input
              data-testid={`rule-labor-qty-per-unit-${rule.id}`}
              type="number"
              step="any"
              value={rule.labor_qty_per_unit ?? 0}
              onChange={u("labor_qty_per_unit")}
              className="w-16 text-right px-1 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
              title="Labor units per quantity unit"
            />
            <span className="text-[var(--jc-text-soft)] text-[10px]">{rule.labor_basis === "job" ? "" : `${rule.labor_basis}/${rule.unit}`}</span>
          </div>
          <div className="flex items-center gap-1 mt-1">
            <span className="text-[var(--jc-text-soft)]">$</span>
            <input
              data-testid={`rule-labor-cost-${rule.id}`}
              type="number"
              step="any"
              value={rule.labor_unit_cost ?? 0}
              onChange={u("labor_unit_cost")}
              className="w-20 text-right px-1 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
            />
            <span className="text-[var(--jc-text-soft)] text-[10px]">/ {rule.labor_basis || "hour"}</span>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-2 mt-2 text-xs">
        <input
          data-testid={`rule-markup-${rule.id}`}
          type="number"
          step="any"
          value={rule.markup_pct ?? ""}
          onChange={u("markup_pct")}
          placeholder={`Markup % (blank = use profile default)`}
          className="px-2 py-1 rounded border border-[var(--jc-border)] font-mono-jc"
        />
        <input
          data-testid={`rule-notes-${rule.id}`}
          value={rule.notes || ""}
          onChange={u("notes")}
          placeholder="Notes (e.g. 3 bundles per square)"
          className="px-2 py-1 rounded border border-[var(--jc-border)]"
        />
      </div>
    </div>
  );
};

export default PricingProfile;
